import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SITE, NAV_LINKS, LOGO, scrollToSection } from "../../config/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_8px_30px_-12px_rgba(17,24,39,0.12)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#home" onClick={(e) => go(e, "#home")} className="flex items-center gap-3" data-testid="nav-logo-link" aria-label="RCS Home">
          <img src={LOGO} alt="RCS — REBHAS Consultancy Services logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
          <span className="hidden sm:block leading-tight">
            <span className="block font-jakarta text-[15px] font-extrabold tracking-tight text-navy">{SITE.short}</span>
            <span className="block font-mono2 text-[9px] uppercase tracking-[0.22em] text-slate-500">Consultancy Services</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className="group relative font-inter text-[13.5px] font-medium text-slate-600 transition-colors duration-300 hover:text-navy"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-consultation-cta"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-jakarta text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-gold"
          >
            Get a Free Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-navy"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle navigation menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-b border-slate-200 bg-white/95 backdrop-blur-xl"
            data-testid="nav-mobile-drawer"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="flex items-center justify-between border-b border-slate-100 py-3 font-jakarta text-base font-semibold text-navy"
                >
                  {l.label}
                  <ArrowUpRight className="h-4 w-4 text-gold" />
                </motion.a>
              ))}
              <button
                onClick={(e) => go(e, "#contact")}
                data-testid="nav-mobile-consultation-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3.5 font-jakarta text-sm font-semibold text-white"
              >
                Get a Free Consultation
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
